'use client';

import React, { useState } from 'react';
import { 
  TextInput, 
  Button, 
  Paper, 
  Title, 
  Select, 
  Stack,
  Text,
  Group
} from '@mantine/core';
import { useAuth } from '../hooks/useAuth';
import { createInvitation } from '../services/firestore';
import { InviteUserFormProps, Invitation } from '../types';

export const InviteUserForm: React.FC<InviteUserFormProps> = ({ 
  orgId,
  onSuccess, 
  onError,
  onInvitationCreated
}) => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('member');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const handleInvite = async () => {
    if (!user || !email || !role) return;
    
    setLoading(true);
    setSuccessMessage('');
    
    try {
      await createInvitation(
        {
          email,
          orgId,
          role: role as 'admin' | 'member' | 'guest',
          invitedBy: user.uid,
          status: 'pending',
          token: '',  // Will be generated by the service
          createdAt: new Date(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
        },
        {
          onInvitationCreated: (invitation: Invitation) => {
            onInvitationCreated?.(invitation);
            onSuccess?.(invitation.token, email, invitation.orgName, invitation.inviterName || '');
          }
        }
      );
      
      setEmail('');
      setSuccessMessage(`Invitation sent to ${email}`);
    } catch (error) {
      console.error('Error sending invitation:', error);
      onError?.(error as Error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Paper radius="md" p="xl" withBorder>
      <Title order={2} mb="md">Invite User</Title>
      
      <Stack gap="md">
        <TextInput
          label="Email Address"
          placeholder="user@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        
        <Select
          label="Role"
          placeholder="Select a role"
          value={role}
          onChange={(value) => setRole(value || 'member')}
          data={[
            { value: 'admin', label: 'Admin' },
            { value: 'member', label: 'Member' },
            { value: 'guest', label: 'Guest' }
          ]}
          required
        />
        
        {successMessage && (
          <Text color="green" fw={500}>{successMessage}</Text>
        )}
        
        <Group justify="flex-end">
          <Button 
            onClick={handleInvite} 
            loading={loading}
          >
            Send Invitation
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}; 